import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet,
  Coins,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCcw,
  Plus,
  Send,
  Loader2,
  Calendar,
  TrendingUp,
  TrendingDown,
  Receipt,
  FileText,
  Search,
  X,
  ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// helpers ────────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const dt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar-IQ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};
const SOURCE_LABEL = {
  receipt: "سند قبض (إدخال نقد)",
  capital: "إيداع رأس مال",
  payment_voucher: "سند دفع (سحب نقد)",
  adjustment: "تعديل يدوي",
};
const PAYEE_LABEL = {
  supplier: "مُجهّز / مورّد",
  employee: "موظف",
  refund_customer: "إرجاع لزبون",
  expense: "مصروف عام",
  other: "أخرى",
};

// Big colored KPI tile ───────────────────────────────────────────────────────
const Kpi = ({ icon: Icon, label, value, sub, color = "#1E3A5F", testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl p-4 shadow-md text-white overflow-hidden relative"
    style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)` }}
  >
    <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none" />
    <div className="relative flex items-center justify-between gap-2">
      <div className="space-y-1 min-w-0">
        <div className="text-[11px] opacity-90 font-bold">{label}</div>
        <div className="font-heading text-2xl font-extrabold tabular-nums truncate" dir="ltr">
          {value}
        </div>
        {sub && <div className="text-[10px] opacity-80">{sub}</div>}
      </div>
      <Icon className="w-9 h-9 opacity-90 shrink-0" />
    </div>
  </div>
);

// Section card wrapper ───────────────────────────────────────────────────────
const Card = ({ title, icon: Icon, action, children, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
  >
    <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-l from-slate-50 to-white border-b border-slate-200">
      <div className="flex items-center gap-2 text-[#1E3A5F] font-bold">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{title}</span>
      </div>
      {action}
    </div>
    <div className="p-3 sm:p-4">{children}</div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────

export default function CashBoxTab() {
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("all"); // all|in|out
  const [search, setSearch] = useState("");

  // forms
  const [capOpen, setCapOpen] = useState(false);
  const [capAmount, setCapAmount] = useState("");
  const [capDesc, setCapDesc] = useState("");
  const [capBusy, setCapBusy] = useState(false);

  const [voucherOpen, setVoucherOpen] = useState(false);
  const [vAmount, setVAmount] = useState("");
  const [vPayeeName, setVPayeeName] = useState("");
  const [vPayeeType, setVPayeeType] = useState("supplier");
  const [vNote, setVNote] = useState("");
  const [vCustomerId, setVCustomerId] = useState("");
  const [vReduceDebt, setVReduceDebt] = useState(false);
  const [vBusy, setVBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, v] = await Promise.all([
        axios.get(`${API}/admin/cash/summary`),
        axios.get(`${API}/admin/cash/transactions?limit=300`),
        axios.get(`${API}/admin/cash/payment-vouchers?limit=200`),
      ]);
      setSummary(s.data);
      setTxs(t.data.transactions || []);
      setVouchers(v.data.vouchers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل بيانات الصندوق");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Fetch customers on demand (called when opening voucher dialog)
  const ensureCustomers = async () => {
    if (customers.length) return;
    try {
      const { data } = await axios.get(`${API}/admin/customers`);
      const list = Array.isArray(data) ? data : data?.customers || data?.users || [];
      setCustomers(list);
    } catch {
      /* silent */
    }
  };

  const openVoucher = () => {
    setVoucherOpen(true);
    ensureCustomers();
  };

  const filteredTxs = useMemo(() => {
    let arr = txs;
    if (filterKind !== "all") arr = arr.filter((t) => t.kind === filterKind);
    const q = search.trim();
    if (q) {
      const low = q.toLowerCase();
      arr = arr.filter(
        (t) =>
          (t.description || "").toLowerCase().includes(low) ||
          (t.actor_name || "").toLowerCase().includes(low) ||
          (SOURCE_LABEL[t.source] || t.source || "").toLowerCase().includes(low) ||
          String(t.amount).includes(q),
      );
    }
    return arr;
  }, [txs, filterKind, search]);

  // Submit capital
  const submitCapital = async () => {
    const amt = Number(capAmount);
    if (!amt || amt <= 0) {
      toast.error("أدخل مبلغ صحيح");
      return;
    }
    setCapBusy(true);
    try {
      await axios.post(`${API}/admin/cash/capital`, {
        amount: amt,
        description: capDesc.trim() || "إيداع رأس مال",
      });
      toast.success(`تم إيداع ${fmt(amt)} د.ع كرأس مال`);
      setCapOpen(false);
      setCapAmount("");
      setCapDesc("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إيداع رأس المال");
    } finally {
      setCapBusy(false);
    }
  };

  // Submit voucher
  const submitVoucher = async () => {
    const amt = Number(vAmount);
    if (!amt || amt <= 0) {
      toast.error("أدخل مبلغ صحيح");
      return;
    }
    if (!vPayeeName.trim()) {
      toast.error("أدخل اسم المستفيد");
      return;
    }
    if (summary && amt > Number(summary.balance || 0)) {
      toast.error(`رصيد الصندوق غير كافٍ — المتاح ${fmt(summary.balance)} د.ع`);
      return;
    }
    setVBusy(true);
    try {
      await axios.post(`${API}/admin/cash/payment-vouchers`, {
        amount: amt,
        payee_name: vPayeeName.trim(),
        payee_type: vPayeeType,
        note: vNote.trim(),
        customer_id: vCustomerId || null,
        reduce_customer_debt: !!(vReduceDebt && vCustomerId),
      });
      toast.success(`تم إصدار سند دفع بقيمة ${fmt(amt)} د.ع`);
      setVoucherOpen(false);
      setVAmount("");
      setVPayeeName("");
      setVPayeeType("supplier");
      setVNote("");
      setVCustomerId("");
      setVReduceDebt(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إصدار سند الدفع");
    } finally {
      setVBusy(false);
    }
  };

  // Reset received counter
  const resetReceived = async () => {
    if (!window.confirm("هل تريد تصفير عدّاد إجمالي المستلم؟ هذا لا يؤثر على الفلوس داخل الصندوق، فقط يعيد العدّاد للصفر اعتباراً من الآن.")) return;
    try {
      await axios.post(`${API}/admin/cash/reset-received`);
      toast.success("تم تصفير عدّاد إجمالي المستلم");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التصفير");
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl" data-testid="cash-box-tab">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-bl from-[#0f5132] via-[#15803d] to-[#0a3622] text-white p-5 shadow-2xl">
        <div className="absolute -top-10 -left-10 w-44 h-44 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-44 h-44 bg-emerald-200/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-yellow-200/90 text-xs font-bold tracking-wider uppercase">
              <Wallet className="w-4 h-4" />
              الخزينة الحيّة
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">
              صندوق النقد <span className="text-yellow-200">(الكاش)</span>
            </h1>
            <p className="text-white/85 text-xs mt-2 max-w-xl leading-relaxed">
              الفلوس الفعلية اللي بصندوقك. يدخلها سندات القبض ورأس المال، ويخرجها سندات الدفع.
              منفصلة تماماً عن دين الزبون.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="cash-edit-balance-btn"
              onClick={async () => {
                // Manual override — when the box's calculated balance
                // doesn't match the cash actually in the safe (printing
                // errors, off-the-books movements, missing receipts), the
                // owner enters the real number and we book a standalone
                // adjustment so the totals reconcile.
                const current = (summary?.balance ?? 0);
                const raw = window.prompt(`الرصيد المحسوب: ${Number(current).toLocaleString("en-US")} د.ع\nأدخل الرصيد الفعلي بالخزنة:`, String(current));
                if (raw == null) return;
                const target = Number(raw);
                if (Number.isNaN(target)) { alert("رقم غير صحيح"); return; }
                const delta = target - Number(current || 0);
                if (delta === 0) return;
                try {
                  await axios.post(`${API}/admin/manual-finance-adjustment`, {
                    kind: "cash_box",
                    amount: delta,
                    reason: `تعديل يدوي للرصيد إلى ${target.toLocaleString("en-US")}`,
                  });
                  await load();
                  toast.success(`تم تحديث الرصيد إلى ${target.toLocaleString("en-US")} د.ع`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "فشل التعديل");
                }
              }}
              className="bg-yellow-400 hover:bg-yellow-300 text-[#0a3622] px-3 py-1.5 rounded-xl text-xs font-extrabold flex items-center gap-1 shadow"
              title="تعديل الرصيد الفعلي بالخزنة يدوياً"
            >
              ✏️ تعديل الرصيد
            </button>
            <button
              data-testid="cash-refresh-btn"
              onClick={load}
              className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          icon={Wallet}
          label="رصيد الصندوق الحالي"
          value={`${fmt(summary?.balance)} د.ع`}
          sub="فلوس فعلية بالخزينة"
          color="#15803d"
          testid="kpi-balance"
        />
        <Kpi
          icon={Coins}
          label="رأس المال المُودَع"
          value={`${fmt(summary?.capital)} د.ع`}
          sub="مجموع إيداعاتك"
          color="#1E3A5F"
          testid="kpi-capital"
        />
        <Kpi
          icon={ArrowDownCircle}
          label="إجمالي المستلم (سندات قبض)"
          value={`${fmt(summary?.received_since_reset?.amount)} د.ع`}
          sub={`${fmt(summary?.received_since_reset?.count)} سند${
            summary?.received_reset_at ? ` • منذ ${dt(summary.received_reset_at)}` : " (الكل)"
          }`}
          color="#0891b2"
          testid="kpi-received"
        />
        <Kpi
          icon={TrendingUp}
          label="إجمالي المبيعات"
          value={`${fmt(summary?.total_sales)} د.ع`}
          sub="كل الفواتير غير المرفوضة"
          color="#7c3aed"
          testid="kpi-sales"
        />
      </div>

      {/* Today / 3-days movement */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { key: "today", title: "حركة اليوم", icon: Calendar, data: summary?.today },
          { key: "three_days", title: "آخر 3 أيام", icon: TrendingUp, data: summary?.three_days },
        ].map((w) => (
          <Card
            key={w.key}
            title={w.title}
            icon={w.icon}
            testid={`window-${w.key}`}
          >
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl py-2">
                <div className="text-[10px] text-emerald-700 font-bold">داخل</div>
                <div className="text-emerald-700 font-extrabold tabular-nums">
                  {fmt(w.data?.ins)}
                </div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-xl py-2">
                <div className="text-[10px] text-rose-700 font-bold">خارج</div>
                <div className="text-rose-700 font-extrabold tabular-nums">
                  {fmt(w.data?.outs)}
                </div>
              </div>
              <div className="bg-slate-100 border border-slate-200 rounded-xl py-2">
                <div className="text-[10px] text-slate-700 font-bold">صافي</div>
                <div
                  className={`font-extrabold tabular-nums ${
                    (w.data?.net || 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                  }`}
                >
                  {fmt(w.data?.net)}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5 text-center">
              {fmt(w.data?.count)} حركة
            </div>
          </Card>
        ))}
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          data-testid="open-capital-btn"
          onClick={() => setCapOpen(true)}
          className="flex items-center justify-center gap-2 bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white font-bold py-3 rounded-2xl shadow-lg hover:shadow-xl active:scale-[0.99] transition"
        >
          <Plus className="w-4 h-4" />
          إيداع رأس مال
        </button>
        <button
          data-testid="open-voucher-btn"
          onClick={openVoucher}
          className="flex items-center justify-center gap-2 bg-gradient-to-l from-rose-600 to-rose-500 text-white font-bold py-3 rounded-2xl shadow-lg hover:shadow-xl active:scale-[0.99] transition"
        >
          <Send className="w-4 h-4" />
          إصدار سند دفع
        </button>
        <button
          data-testid="reset-received-btn"
          onClick={resetReceived}
          className="flex items-center justify-center gap-2 bg-white border-2 border-cyan-500 text-cyan-700 font-bold py-3 rounded-2xl shadow-sm hover:bg-cyan-50 active:scale-[0.99] transition"
        >
          <RefreshCcw className="w-4 h-4" />
          تصفير عدّاد المستلم
        </button>
      </div>

      {/* Transactions table */}
      <Card
        title={`حركات الصندوق (${filteredTxs.length})`}
        icon={Receipt}
        testid="cash-tx-card"
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="cash-tx-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="بحث…"
                className="border border-slate-300 rounded-lg pr-7 pl-2 py-1 text-xs w-32"
              />
            </div>
            <select
              data-testid="cash-tx-filter"
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value)}
              className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white"
            >
              <option value="all">الكل</option>
              <option value="in">داخل فقط</option>
              <option value="out">خارج فقط</option>
            </select>
          </div>
        }
      >
        {filteredTxs.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-8">لا توجد حركات</div>
        ) : (
          <div className="overflow-auto max-h-[480px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-slate-700">
                  <th className="text-right p-2 font-bold">التاريخ</th>
                  <th className="text-right p-2 font-bold">النوع</th>
                  <th className="text-right p-2 font-bold">المصدر</th>
                  <th className="text-right p-2 font-bold">الوصف</th>
                  <th className="text-right p-2 font-bold">المستخدم</th>
                  <th className="text-left p-2 font-bold">المبلغ</th>
                  <th className="text-left p-2 font-bold">رصيد بعد</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxs.map((t, i) => (
                  <tr
                    key={t.id || i}
                    data-testid="cash-tx-row"
                    className="border-t border-slate-100 hover:bg-slate-50/50"
                  >
                    <td className="p-2 text-slate-600 whitespace-nowrap">{dt(t.created_at)}</td>
                    <td className="p-2">
                      {t.kind === "in" ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">
                          <ArrowDownCircle className="w-3 h-3" /> داخل
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-bold">
                          <ArrowUpCircle className="w-3 h-3" /> خارج
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-slate-700">
                      {SOURCE_LABEL[t.source] || t.source || "—"}
                    </td>
                    <td className="p-2 text-slate-600 max-w-[260px] truncate" title={t.description}>
                      {t.description || "—"}
                    </td>
                    <td className="p-2 text-slate-600">{t.actor_name || "—"}</td>
                    <td
                      className={`p-2 text-left tabular-nums font-extrabold ${
                        t.kind === "in" ? "text-emerald-700" : "text-rose-700"
                      }`}
                      dir="ltr"
                    >
                      {t.kind === "in" ? "+" : "−"}
                      {fmt(t.amount)}
                    </td>
                    <td className="p-2 text-left tabular-nums text-slate-700" dir="ltr">
                      {fmt(t.balance_after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Vouchers table */}
      <Card title={`سندات الدفع (${vouchers.length})`} icon={FileText} testid="vouchers-card">
        {vouchers.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-8">لا توجد سندات دفع</div>
        ) : (
          <div className="overflow-auto max-h-[320px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-slate-700">
                  <th className="text-right p-2 font-bold">التاريخ</th>
                  <th className="text-right p-2 font-bold">المستفيد</th>
                  <th className="text-right p-2 font-bold">النوع</th>
                  <th className="text-right p-2 font-bold">ملاحظة</th>
                  <th className="text-right p-2 font-bold">المستخدم</th>
                  <th className="text-left p-2 font-bold">المبلغ</th>
                  <th className="text-center p-2 font-bold">خصم دين؟</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v, i) => (
                  <tr
                    key={v.id || i}
                    data-testid="voucher-row"
                    className="border-t border-slate-100 hover:bg-slate-50/50"
                  >
                    <td className="p-2 text-slate-600 whitespace-nowrap">{dt(v.created_at)}</td>
                    <td className="p-2 font-bold text-slate-800">{v.payee_name || "—"}</td>
                    <td className="p-2 text-slate-700">
                      {PAYEE_LABEL[v.payee_type] || v.payee_type || "—"}
                    </td>
                    <td className="p-2 text-slate-600 max-w-[220px] truncate" title={v.note}>
                      {v.note || "—"}
                    </td>
                    <td className="p-2 text-slate-600">{v.actor_name || "—"}</td>
                    <td className="p-2 text-left tabular-nums text-rose-700 font-extrabold" dir="ltr">
                      −{fmt(v.amount)}
                    </td>
                    <td className="p-2 text-center">
                      {v.reduced_customer_debt ? (
                        <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">
                          نعم
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ─────────── Capital Dialog ─────────── */}
      {capOpen && (
        <Modal
          title="إيداع رأس مال"
          icon={Coins}
          onClose={() => !capBusy && setCapOpen(false)}
          color="#1E3A5F"
        >
          <div className="space-y-3">
            <Field label="المبلغ (د.ع)">
              <input
                data-testid="capital-amount"
                type="number"
                inputMode="numeric"
                value={capAmount}
                onChange={(e) => setCapAmount(e.target.value)}
                className="w-full border-2 border-slate-300 rounded-xl px-3 py-2 font-bold text-lg tabular-nums focus:border-[#3B82F6] outline-none"
                placeholder="مثلاً 5000000"
                dir="ltr"
              />
            </Field>
            <Field label="ملاحظة (اختياري)">
              <input
                data-testid="capital-desc"
                value={capDesc}
                onChange={(e) => setCapDesc(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:border-[#3B82F6] outline-none"
                placeholder="مصدر الإيداع…"
              />
            </Field>
            <div className="flex gap-2 pt-1">
              <button
                data-testid="capital-cancel"
                onClick={() => setCapOpen(false)}
                disabled={capBusy}
                className="flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                data-testid="capital-submit"
                onClick={submitCapital}
                disabled={capBusy}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white font-bold flex items-center justify-center gap-1 disabled:opacity-50"
              >
                {capBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                إيداع
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ─────────── Voucher Dialog ─────────── */}
      {voucherOpen && (
        <Modal
          title="إصدار سند دفع"
          icon={Send}
          onClose={() => !vBusy && setVoucherOpen(false)}
          color="#9f1239"
        >
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-[11px] text-amber-800">
              الرصيد المتاح بالصندوق: <b dir="ltr">{fmt(summary?.balance)}</b> د.ع
            </div>
            <Field label="المبلغ (د.ع)">
              <input
                data-testid="voucher-amount"
                type="number"
                inputMode="numeric"
                value={vAmount}
                onChange={(e) => setVAmount(e.target.value)}
                className="w-full border-2 border-slate-300 rounded-xl px-3 py-2 font-bold text-lg tabular-nums focus:border-rose-500 outline-none"
                placeholder="0"
                dir="ltr"
              />
            </Field>
            <Field label="اسم المستفيد">
              <input
                data-testid="voucher-payee"
                value={vPayeeName}
                onChange={(e) => setVPayeeName(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:border-rose-500 outline-none"
                placeholder="اسم الشخص / الشركة"
              />
            </Field>
            <Field label="نوع المستفيد">
              <div className="relative">
                <select
                  data-testid="voucher-type"
                  value={vPayeeType}
                  onChange={(e) => setVPayeeType(e.target.value)}
                  className="w-full appearance-none border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-rose-500 outline-none"
                >
                  <option value="supplier">مُجهّز / مورّد</option>
                  <option value="employee">موظف</option>
                  <option value="refund_customer">إرجاع لزبون</option>
                  <option value="expense">مصروف عام</option>
                  <option value="other">أخرى</option>
                </select>
                <ChevronDown className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </Field>
            <Field label="ملاحظة (اختياري)">
              <input
                data-testid="voucher-note"
                value={vNote}
                onChange={(e) => setVNote(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:border-rose-500 outline-none"
                placeholder="السبب أو التفاصيل…"
              />
            </Field>

            {/* Customer linkage (optional) */}
            {(vPayeeType === "refund_customer" || vCustomerId) && (
              <Field label="ربط بزبون (اختياري)">
                <div className="relative">
                  <select
                    data-testid="voucher-customer"
                    value={vCustomerId}
                    onChange={(e) => setVCustomerId(e.target.value)}
                    className="w-full appearance-none border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-rose-500 outline-none"
                  >
                    <option value="">— بدون ربط —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.full_name || c.phone} — {c.phone}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </Field>
            )}

            {vPayeeType !== "refund_customer" && !vCustomerId && (
              <button
                type="button"
                onClick={() => setVCustomerId(" ")}
                className="text-xs text-rose-600 hover:underline"
              >
                + ربط هذا السند بزبون لخصم دينه
              </button>
            )}

            {vCustomerId && vCustomerId.trim() && (
              <label className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-2 cursor-pointer">
                <input
                  data-testid="voucher-reduce-debt"
                  type="checkbox"
                  checked={vReduceDebt}
                  onChange={(e) => setVReduceDebt(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-xs text-emerald-900 leading-relaxed">
                  خصم هذا المبلغ من دين الزبون المختار (سيُعاد حساب الرصيد فوراً).
                </span>
              </label>
            )}

            <div className="flex gap-2 pt-1">
              <button
                data-testid="voucher-cancel"
                onClick={() => setVoucherOpen(false)}
                disabled={vBusy}
                className="flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                data-testid="voucher-submit"
                onClick={submitVoucher}
                disabled={vBusy}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-l from-rose-600 to-rose-500 text-white font-bold flex items-center justify-center gap-1 disabled:opacity-50"
              >
                {vBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                إصدار السند
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Small reusable bits ────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-bold text-slate-700 mb-1">{label}</span>
    {children}
  </label>
);

const Modal = ({ title, icon: Icon, color = "#1E3A5F", onClose, children }) => (
  <div
    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
    onClick={onClose}
    data-testid="cash-modal"
  >
    <div
      className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      dir="rtl"
    >
      <div
        className="flex items-center justify-between px-4 py-3 text-white"
        style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)` }}
      >
        <div className="flex items-center gap-2 font-bold">
          {Icon && <Icon className="w-5 h-5" />}
          <span>{title}</span>
        </div>
        <button
          onClick={onClose}
          className="bg-white/20 hover:bg-white/30 rounded-full w-7 h-7 flex items-center justify-center"
          data-testid="modal-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);
